import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core"
import { ActivatedRoute, Params } from "@angular/router"
import { CHNode } from "../../models/node.model"
import { BrowsingService } from "../../services/browsing.service"
import { ColorCodeService } from "../../services/color-code.service"
import { GTMService } from "../../services/gtm.service"
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout"
import { asyncScheduler, Observable, Subject } from "rxjs"
import { filter, map, switchMap, takeUntil, tap } from "rxjs/operators"
import { APIResponse } from "../../models/apiResponse.model"
import { Resource } from "../../models/resource/resource"
import { LearningObject } from "../../models/learningObject.model"
import Utils from "../../utils/utils"
import { RankByComponent } from "../../shared-components/results-group/rank-by/rank-by.component"
import { routeAnimations, routeTransition } from "../../utils/animations"

enum BrowsingResultsBreakpoints {
    isSearchCard = "(max-width: 605px)",
    isTablet = "(max-width: 768px)",
}

@Component({
    selector: "app-browsing-results-group",
    templateUrl: "./browsing-results-group.component.html",
    styleUrls: ["./browsing-results-group.component.scss"],
    host: { "[@routeTransition]": "true", "[@routeAnimations]": "false", style: "display: block;" },
    animations: [routeTransition, routeAnimations],
    standalone: false,
})
export class BrowsingResultsGroupComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild("rankByComponent") rankByComponent: RankByComponent
    @Input() resultsPerRow: number = 3 //default
    displayClearFilters: boolean = false
    activeNode: CHNode
    colorCode: string = "#fff"
    bootstrapClasses: string = ""
    latestRequestedQueryParams: Params = null
    latestRequestedSlug: string = null
    gtmPrefix: string = "gtm-results-browse-card"

    public nextPageURL$: Observable<string> = this.browsingService.nextPageURL$
    public count$: Observable<number> = this.browsingService.count$
    public breakPoints$: Observable<{ isTablet: boolean; isSearchCard: boolean }>
    public loading$: Observable<boolean> = this.browsingService.loading$
    private destroyed$: Subject<void> = new Subject()
    public handleRouteQueryParamsChanges$: Observable<Params> = this.route.queryParams.pipe(
        takeUntil(this.destroyed$),
        tap(() => this.browsingService.resetPagination()),
        tap((queryParams: Params) => (this.displayClearFilters = "selected_facet" in queryParams)),
        tap((queryParams: Params) => {
            if (this.queryParamsChanged(queryParams)) this.browsingService.emitLastNodeSection()
        })
    )
    public resources$: Observable<LearningObject[]>

    constructor(
        protected browsingService: BrowsingService,
        private route: ActivatedRoute,
        private colorCodeService: ColorCodeService,
        private gtmService: GTMService,
        private breakpointObserver: BreakpointObserver
    ) {
        this.colorCode = this.colorCodeService.getPrimaryColor()
    }

    public ngOnInit() {
        this.setBreakpoints()
        this.handleRouteQueryParamsChanges()
        this.getResourcesOnNodeChanges()
        this.subscribeToResources()
    }

    /**
     * I'm sorry you have to see this.
     * But until we rewrite all filters handling in this project we can't set a default in any other way.
     * I tried, trust me
     */
    public ngAfterViewInit() {
        asyncScheduler.schedule(() => {
            this.rankByComponent.checkContext()
            if (!this.route.snapshot.queryParams?.rank_by) {
                this.rankByComponent.changeRanking("recency")
            }
        })
    }

    public subscribeToResources(): void {
        this.resources$ = this.browsingService.resources$
    }

    public getResourcesOnNodeChanges(): void {
        this.browsingService.activeNodeChanged
            .pipe(
                takeUntil(this.destroyed$),
                tap(() => (this.browsingService.loading = true)),
                filter((node: CHNode) => this.isNodeLoaded(node)),
                filter((node: CHNode) => this.isNewNode(node)),
                filter((node: CHNode) => this.haveQueryParamsChanged(node)),
                tap(() => this.handleGTM()),
                tap((node: CHNode) => this.setCurrentActiveNode(node)),
                filter((node: CHNode) => this.haveQueryParamsAndNodeChanged(node)),
                tap((node: CHNode) => this.setLatestRequestedSlug(node)),
                tap(() => this.prepareComponentStateForReceivingData()),
                switchMap((node: CHNode) => this.getResources(node))
            )
            .subscribe()
    }

    public isNodeLoaded(node: CHNode): boolean {
        return node.isLoaded()
    }

    public isNewNode(node: CHNode): boolean {
        const currentRouteSlug: string = CHNode.cleanSlug(this.route.snapshot.params.nodeSlug)
        return currentRouteSlug === node.slug
    }

    public haveQueryParamsChanged(node: CHNode): boolean {
        const currentParams: Params = this.route.snapshot.queryParams
        const isSameNode: boolean = !!this.activeNode && node.id === this.activeNode.id
        return !(
            isSameNode &&
            JSON.stringify(currentParams) === JSON.stringify(this.latestRequestedQueryParams)
        )
    }

    public haveQueryParamsAndNodeChanged(node: CHNode = null): boolean {
        const currentParams: Params = this.route.snapshot.queryParams
        return !(
            node.slug == this.latestRequestedSlug &&
            JSON.stringify(this.latestRequestedQueryParams) == JSON.stringify(currentParams)
        )
    }

    public setCurrentActiveNode(node: CHNode): void {
        this.activeNode = node
        this.colorCode = this.colorCodeService.getColorForNode(node)
    }

    public handleGTM(): void {
        this.gtmService.resetDataLayer()
        this.gtmService.pushPageView()
    }

    public handleRouteQueryParamsChanges(): void {
        this.handleRouteQueryParamsChanges$.subscribe()
    }

    public setBreakpoints(): void {
        this.breakPoints$ = this.observeBreakpoints(
            BrowsingResultsBreakpoints.isSearchCard,
            BrowsingResultsBreakpoints.isTablet
        )
    }

    public setLatestRequestedSlug(node: CHNode): void {
        if (node) {
            this.latestRequestedSlug = node.slug
        } else {
            this.latestRequestedSlug = CHNode.cleanSlug(this.route.snapshot.params.nodeSlug)
        }
    }

    public prepareComponentStateForReceivingData(): void {
        this.latestRequestedQueryParams = this.route.snapshot.queryParams
        this.browsingService.resetPagination()
    }

    public getResources(node: CHNode): Observable<APIResponse<Resource>> {
        const nodeIdOrSlug: string = this.getNodeIdOrSlug(node)
        return this.browsingService.getResources(this.latestRequestedQueryParams, nodeIdOrSlug)
    }

    public getNodeIdOrSlug(node: CHNode): string {
        return node ? node.id.toString() : CHNode.cleanSlug(this.route.snapshot.params.nodeSlug)
    }

    /**
     * Observe specific media queries on window resize
     * @private
     */
    private observeBreakpoints(...args): Observable<{ isTablet: boolean; isSearchCard: boolean }> {
        if (!args) return

        return this.breakpointObserver.observe([...args]).pipe(
            map(
                (
                    breakpointState: BreakpointState
                ): {
                    isSearchCard: boolean
                    isTablet: boolean
                } => ({
                    isTablet: breakpointState.breakpoints[BrowsingResultsBreakpoints.isTablet],
                    isSearchCard:
                        breakpointState.breakpoints[BrowsingResultsBreakpoints.isSearchCard],
                })
            )
        )
    }

    public queryParamsChanged(queryParams: Params): boolean {
        return (
            !!this.latestRequestedQueryParams &&
            JSON.stringify(this.latestRequestedQueryParams) !== JSON.stringify(queryParams)
        )
    }

    public loadNextPage(): void {
        this.browsingService.incrementPageNumber()
        this.getResources(this.activeNode).subscribe()
    }

    public onAdvancedSearch(): void {
        sessionStorage.setItem("avoid-setting-grade-band", "true")
        const queryParams = {
            ...this.route.snapshot.queryParams,
            ...this.buildQueryParamsForAdvancedSearch(),
        }

        const url: string = "/search/?" + Utils.serializeQueryParams(queryParams)
        window.open(url, "_blank")
    }

    public buildQueryParamsForAdvancedSearch(): { selected_facet?: string[]; student?: boolean } {
        let queryParams: { selected_facet?: string[]; student?: boolean } = {}
        queryParams.selected_facet = this.getSelectedFacets()

        if (this.latestRequestedQueryParams?.student) {
            queryParams.student = true
        }
        return queryParams
    }

    public getSelectedFacets(): string[] {
        let selectedFacets: string[] = [`subject:${this.activeNode.id}`]
        if (Array.isArray(this.latestRequestedQueryParams?.selected_facet)) {
            selectedFacets = [...this.latestRequestedQueryParams.selected_facet, ...selectedFacets]
        } else if (this.latestRequestedQueryParams.selected_facet) {
            selectedFacets = [this.latestRequestedQueryParams.selected_facet, ...selectedFacets]
        }
        return selectedFacets
    }

    ngOnDestroy() {
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
